import {useCallback, useMemo, useState} from 'react';
import type {
    BodyScrollEndEvent,
    ColDef,
    GetRowIdParams,
    ICellRendererParams,
    ValueGetterParams
} from 'ag-grid-community';
import {AppGrid, GridLoading} from 'components/common/ag-grid/AppGrid.component';
import {ConfigurationPage} from 'components/common/configuration/ConfigurationPage.component';
import {
    configurationGridHeight,
    gridColumn,
    isAgGridInConfigurationEnabled,
    ROW_HEIGHT
} from 'components/common/ag-grid/configuration/appConfigurationGrid.function';
import {isLastRowRendered, useAvailableHeight} from 'components/common/ag-grid/appGrid.function';
import SearchGroupInput from '../input/SearchGroupInput.component';
import {SegmentedControl} from 'components/common/SegmentedControl.component';
import {useTranslation} from 'components/translations/useTranslation';
import {Managers} from './Managers.component';
import {useNearestManagers} from './useNearestManagers';
import {EMPLOYEE_BY_MANAGER, MANAGER_BY_EMPLOYEE, NearestManagerRow} from 'types/managers';
import type {Translate} from 'types/myTasks';
import './newManagers.scss';

// the page, as its class names and ids spell it
const PAGE = 'managers';

const LEFT_COL = 'left';
const RIGHT_COL = 'right';

// the grid fills the page, keeping the card's own padding and the page's bottom margin below it
const GRID_HEIGHT = configurationGridHeight(PAGE);

/** One relation, split into the two sides the columns show. */
interface ManagerGridRow {
    id: string;
    leftName: string;
    leftEmail: string;
    rightName: string;
    rightEmail: string;
}

type NameField = 'leftName' | 'rightName';
type EmailField = 'leftEmail' | 'rightEmail';

/** The name on the first line, the email under it - as the classic list rendered both columns. */
const personRenderer = (nameField: NameField, emailField: EmailField) =>
    (params: ICellRendererParams<ManagerGridRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
            <span className="app-grid__person">
                <span className="text-body">{row[nameField]}</span>
                <span className="new-managers__email text-body-secondary">{row[emailField]}</span>
            </span>
        );
    };

function buildRowData(rows: NearestManagerRow[], isManagerByEmployee: boolean): ManagerGridRow[] {
    return rows.map((row, index) => {
        const employeeName = `${row.employeeFirstName} ${row.employeeLastName}`.trim();
        const managerName = `${row.managerFirstName} ${row.managerLastName}`.trim();
        // the endpoint pages over relations rather than people, so neither side is unique on
        // its own: the position in the list is what tells two rows of a pair apart
        const id = `${row.employeeEmail}-${row.managerEmail}-${index}`;

        return isManagerByEmployee
            ? {
                id,
                leftName: employeeName, leftEmail: row.employeeEmail,
                rightName: managerName, rightEmail: row.managerEmail
            }
            : {
                id,
                leftName: managerName, leftEmail: row.managerEmail,
                rightName: employeeName, rightEmail: row.employeeEmail
            };
    });
}

/** The leading column names the employee in the manager-by-employee mode, and the manager in the other. */
function columnLabel(colId: string, isManagerByEmployee: boolean, translate: Translate): string {
    const isEmployee = (colId === LEFT_COL) === isManagerByEmployee;
    return translate(isEmployee ? 'managers.employee' : 'managers.manager');
}

/**
 * ag-grid rendering of the Configuration -> Managers page, behind
 * fe-use-ng-grid-in-configuration. The classic list it replaces is in Managers.component.
 */
export const NewManagers = () => {

    const {translate} = useTranslation();
    const {
        rows,
        isLoading,
        searchValue,
        isManagerByEmployee,
        inputChange,
        clearSearch,
        onModeChange,
        loadMoreItems
    } = useNearestManagers();

    // the grid owns its scrolling area, and the loader stands in its place until the first page
    // lands. Whichever of them is mounted is the element measured, which is why it arrives
    // through a callback
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const rowData = useMemo(() => buildRowData(rows, isManagerByEmployee), [rows, isManagerByEmployee]);

    // a reload keeps the rows on screen; only the very first page is waited for
    const showGrid = !isLoading || rowData.length > 0;

    const columnDefs = useMemo<ColDef<ManagerGridRow>[]>(() => {
        const personColumn = (colId: string, nameField: NameField, emailField: EmailField) =>
            gridColumn<ManagerGridRow>({
                colId,
                headerName: columnLabel(colId, isManagerByEmployee, translate),
                // the two columns split the grid evenly, as the classic list's col-md-6 did
                flex: 1,
                minWidth: 160,
                // the list is paged, so sorting here would only order the pages already loaded
                sortable: false,
                valueGetter: (params: ValueGetterParams<ManagerGridRow>) => params.data?.[nameField],
                cellRenderer: personRenderer(nameField, emailField),
                // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked
                // for explicitly - it carries both lines, since either one can be cut
                tooltipValueGetter: (params) => params.data
                    ? `${params.data[nameField]} (${params.data[emailField]})`
                    : undefined
            });

        return [
            personColumn(LEFT_COL, 'leftName', 'leftEmail'),
            personColumn(RIGHT_COL, 'rightName', 'rightEmail')
        ];
    }, [isManagerByEmployee, translate]);

    /**
     * The next page is asked for once the grid is scrolled to its last row. ag-grid virtualises
     * the rows, so there is no scrolling element of our own to watch for the bottom.
     */
    const onBodyScrollEnd = useCallback((event: BodyScrollEndEvent<ManagerGridRow>) => {
        if (isLastRowRendered(event.api)) {
            loadMoreItems();
        }
    }, [loadMoreItems]);

    return (
        <ConfigurationPage page={PAGE}
                           titleKey="managers.header"
                           infoKey="managers.headerInfo"
                           translate={translate}>

            <div className="d-flex justify-content-end gap-3 align-items-center mb-24">
                <SearchGroupInput placeholder={translate("managers.search")}
                                  value={searchValue}
                                  className="ga-input__default"
                                  onInputChange={inputChange}
                                  onClearInput={clearSearch}
                                  translate={translate}/>

                <SegmentedControl
                    options={[
                        {
                            key: MANAGER_BY_EMPLOYEE,
                            label: translate("managers.managerByEmployee"),
                            isSelected: isManagerByEmployee,
                            onClick: () => onModeChange(MANAGER_BY_EMPLOYEE)
                        },
                        {
                            key: EMPLOYEE_BY_MANAGER,
                            label: translate("managers.employeesByManager"),
                            isSelected: !isManagerByEmployee,
                            onClick: () => onModeChange(EMPLOYEE_BY_MANAGER)
                        }
                    ]}
                />
            </div>

            {showGrid
                ? <AppGrid<ManagerGridRow>
                    className="new-managers__grid"
                    dataCy="managers.grid"
                    containerRef={setGridElement}
                    height={gridHeight}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    getRowId={(params: GetRowIdParams<ManagerGridRow>) => params.data.id}
                    rowHeight={ROW_HEIGHT}
                    onBodyScrollEnd={onBodyScrollEnd}
                    localeText={{noRowsToShow: translate('managers.noMatches')}}
                />
                : <GridLoading containerRef={setGridElement} height={gridHeight}/>
            }
        </ConfigurationPage>
    );
};

/**
 * Configuration -> Managers. The flag is read here rather than around the route, so the page
 * itself decides which of its two renderings it is.
 */
export const ManagersWrapper = () => isAgGridInConfigurationEnabled() ? <NewManagers/> : <Managers/>;

export default ManagersWrapper;
